import React from 'react';

interface PortableTextProps {
  value: any;
  className?: string;
}

/**
 * Simple component for rendering Portable Text content from Sanity
 * This version doesn't rely on external packages
 */
const PortableText: React.FC<PortableTextProps> = ({ value, className }) => {
  if (!value) {
    return null;
  }

  // If the value is a string (legacy content), render it as HTML
  if (typeof value === 'string') {
    return <div className={className} dangerouslySetInnerHTML={{ __html: value }} />;
  }

  // If the value is an array (Portable Text format), render it
  if (Array.isArray(value)) {
    return (
      <div className={className}>
        {value.map((block, blockIndex) => {
          // Handle different block types
          if (block._type === 'block') {
            // Determine the element type based on the style
            let BlockElement: keyof JSX.IntrinsicElements = 'p';
            let blockClass = 'mb-4';

            switch (block.style) {
              case 'h1':
                BlockElement = 'h1';
                blockClass = 'text-2xl font-bold mb-4';
                break;
              case 'h2':
                BlockElement = 'h2';
                blockClass = 'text-xl font-bold mb-3';
                break;
              case 'h3':
                BlockElement = 'h3';
                blockClass = 'text-lg font-bold mb-2';
                break;
              case 'h4':
                BlockElement = 'h4';
                blockClass = 'text-base font-bold mb-2';
                break;
              case 'blockquote':
                BlockElement = 'blockquote';
                blockClass = 'border-l-4 border-gray-300 pl-4 italic my-4';
                break;
              default:
                BlockElement = 'p';
                blockClass = 'mb-4';
            }

            // Handle list items
            if (block.listItem) {
              if (block.listItem === 'bullet') {
                return (
                  <ul key={block._key || blockIndex} className="list-disc pl-5 mb-4">
                    <li>
                      {block.children?.map((child: any, childIndex: number) => (
                        <span key={child._key || childIndex}>{child.text}</span>
                      ))}
                    </li>
                  </ul>
                );
              } else if (block.listItem === 'number') {
                return (
                  <ol key={block._key || blockIndex} className="list-decimal pl-5 mb-4">
                    <li>
                      {block.children?.map((child: any, childIndex: number) => (
                        <span key={child._key || childIndex}>{child.text}</span>
                      ))}
                    </li>
                  </ol>
                );
              }
            }

            // Render regular block
            return (
              <BlockElement key={block._key || blockIndex} className={blockClass}>
                {block.children?.map((child: any, childIndex: number) => {
                  // Handle marks (formatting)
                  if (child.marks && child.marks.length > 0) {
                    // Check for link mark
                    const linkMark = block.markDefs?.find((def: any) =>
                      def._type === 'link' && child.marks.includes(def._key)
                    );

                    if (linkMark) {
                      return (
                        <a
                          key={child._key || childIndex}
                          href={linkMark.href}
                          className="text-blue-500 hover:underline"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {child.text}
                        </a>
                      );
                    }

                    // Handle other marks (bold, italic, etc.)
                    let textContent = child.text || '';

                    // Apply marks in a nested way
                    let textElement = <>{textContent}</>;

                    // Apply marks in a specific order for proper nesting
                    if (child.marks.includes('strike-through')) {
                      textElement = <s>{textElement}</s>;
                    }
                    if (child.marks.includes('underline')) {
                      textElement = <u>{textElement}</u>;
                    }
                    if (child.marks.includes('em')) {
                      textElement = <em>{textElement}</em>;
                    }
                    if (child.marks.includes('strong')) {
                      textElement = <strong>{textElement}</strong>;
                    }

                    return <span key={child._key || childIndex}>{textElement}</span>;
                  }

                  // Regular text
                  return <span key={child._key || childIndex}>{child.text || ''}</span>;
                })}
              </BlockElement>
            );
          }

          // Handle image blocks
          if (block._type === 'image' && block.asset?._ref) {
            const imageUrl = `https://cdn.sanity.io/images/${process.env.NEXT_PUBLIC_SANITY_PROJECT_ID || 'lirfyxoo'}/${process.env.NEXT_PUBLIC_SANITY_DATASET || 'production'}/${block.asset._ref.replace('image-', '').replace('-jpg', '.jpg').replace('-png', '.png').replace('-webp', '.webp')}`;

            return (
              <div key={block._key || blockIndex} className="my-4">
                <img
                  src={imageUrl}
                  alt={block.alt || 'Image'}
                  className="max-w-full h-auto"
                />
                {block.caption && (
                  <p className="text-sm text-gray-500 mt-1">{block.caption}</p>
                )}
              </div>
            );
          }

          // Return null for other unsupported block types
          return null;
        })}
      </div>
    );
  }

  // Handle case where value is an object but not an array
  // This is likely a direct block object rather than an array of blocks
  if (typeof value === 'object' && value !== null) {
    // If it's a single block, wrap it in an array and process it
    if (value._type === 'block' || value._type === 'image') {
      return <PortableText value={[value]} className={className} />;
    }

    // If it has a children property that's an array, it might be a direct block object
    if (value.children && Array.isArray(value.children)) {
      // Create a proper block object
      const block = {
        _type: 'block',
        _key: value._key || 'fallback-key',
        style: value.style || 'normal',
        children: value.children
      };
      return <PortableText value={[block]} className={className} />;
    }
  }

  // Fallback for unknown formats
  return <div className={className}>{JSON.stringify(value)}</div>;
};

export default PortableText;
